<template>
  <div class="products-revisions-view">
    <revision-manager
      :fields="fields"
      :parent="product"
      :revisions="product.revisions"
      @add="addRevision"
      @delete="deleteRevision"
      @edit="editRevision"
      v-if="isSuccessfullyLoaded"
    >
      <span slot="title">
        <router-link
          :to="{ name: 'product', params: { id: product.id }}"
        >{{product.name}}</router-link>
      </span>

      <!-- Alt Name -->
      <template scope="altNameProps" slot="altName">
        <inheritable-value
          :inherited-value="product.altName"
          :value="altNameProps.value"
        />
      </template>

      <!-- Delivered Items -->
      <template scope="deliveredItemsProps" slot="deliveredItems">
        <inheritable-value
          :inherited-value="product.deliveredItems"
          :value="deliveredItemsProps.value"
        >
          <template scope="{ value }" slot="value">
            <list :items="value" class="uk-margin-remove" type="bullet" />
          </template>
        </inheritable-value>
      </template>

      <!-- Description -->
      <template scope="descriptionProps" slot="description">
        <inheritable-value
          :inherited-value="product.description"
          :value="descriptionProps.value"
        />
      </template>

      <!-- Features -->
      <template scope="featuresProps" slot="features">
        <inheritable-value
          :inherited-value="product.features"
          :value="featuresProps.value"
        >
          <template scope="{ value }" slot="value">
            <definition-list :items="value" class="uk-margin-remove" />
          </template>
        </inheritable-value>
      </template>

      <!-- Legal Texts -->
      <template scope="legalTextsProps" slot="legalTexts">
        <inheritable-value
          :inherited-value="product.legalTexts"
          :value="legalTextsProps.value"
        >
          <template scope="{ value }" slot="value">
            <legal-texts-cell :legal-texts="value" />
          </template>
        </inheritable-value>
      </template>

      <!-- Keywords -->
      <template scope="keywordsProps" slot="keywords">
        <inheritable-value
          :inherited-value="product.keywords"
          :value="keywordsProps.value"
        >
          <template scope="{ value }" slot="value">
            <separated-list :items="value" />
          </template>
        </inheritable-value>
      </template>

      <!-- Media -->
      <template scope="mediaProps" slot="media">
        <inheritable-value
          :inherited-value="product.media"
          :value="mediaProps.value"
        >
          <template scope="{ value }" slot="value">
            <gallery-cell :attachments="value" />
          </template>
        </inheritable-value>
      </template>

      <!-- Summary Texts -->
      <template scope="summaryTextsProps" slot="summaryTexts">
        <inheritable-value
          :inherited-value="product.summaryTexts"
          :value="summaryTextsProps.value"
        >
          <template scope="{ value }" slot="value">
            <list :items="value" class="uk-margin-remove" type="bullet">
              <text-placeholder slot="empty" />
            </list>
          </template>
        </inheritable-value>
      </template>

      <!-- USPs -->
      <template scope="uspsProps" slot="usps">
        <inheritable-value
          :inherited-value="product.usps"
          :value="uspsProps.value"
        >
          <template scope="{ value }" slot="value">
            <list :items="value" class="uk-margin-remove" type="bullet">
              <text-placeholder slot="empty" />
            </list>
          </template>
        </inheritable-value>
      </template>

      <template scope="alwaysDisplayedProps" slot="alwaysDisplayed">
        <inheritable-value
          :inherited-value="product.alwaysDisplayed"
          :value="alwaysDisplayedProps.value"
        />
      </template>
      <template scope="pxsdbSecureBufferDisabledProps" slot="pxsdbSecureBufferDisabled">
        <inheritable-value
          :inherited-value="product.pxsdbSecureBufferDisabled"
          :value="pxsdbSecureBufferDisabledProps.value"
        />
      </template>
      <template scope="tagsProps" slot="tags">
        <inheritable-value
          :inherited-value="product.tags"
          :value="tagsProps.value"
        >
          <template scope="{ value }" slot="value">
            <separated-list :items="value" />
          </template>
        </inheritable-value>
      </template>

      <template scope="storageProps" slot="storage">
        <inheritable-value
          :inherited-value="product.storage ? `${product.storage.value} ${product.storage.unit}` : null"
          :value="storageProps.value ? `${storageProps.value.value} ${storageProps.value.unit}` : null"
        />
      </template>

      <template scope="memoryProps" slot="memory">
        <inheritable-value
          :inherited-value="product.memory"
          :value="memoryProps.value ? `${memoryProps.value.value} ${memoryProps.value.unit}` : null"
        />
      </template>

     <!-- Skip Stock Update -->
     <template scope="stockSkipUpdateFromProps" slot="stockSkipUpdateFrom">
        <inheritable-value
          :inherited-value="product.stockSkipUpdateFrom"
          :value="stockSkipUpdateFromProps.value"
          :is-date="true"
        />
      </template>
     <template scope="stockSkipUpdateTorops" slot="stockSkipUpdateTo">
        <inheritable-value
          :inherited-value="product.stockSkipUpdateTo"
          :value="stockSkipUpdateTorops.value"
          :is-date="true"
        />
      </template>
    </revision-manager>

    <div
      class="uk-text-center uk-margin-large uk-margin-large-top uk-margin-large-left uk-margin-large-right"
      v-else
    >
      <loader />
    </div>
  </div>
</template>

<script>
import pull from 'lodash/pull';
import Color from '@/components/Color';
import DefinitionList from '@/components/DefinitionList';
import deleteProductRevisionMutation
  from '@/api/mutations/delete-product-revision.graphql';
import Dimensions from '@/components/Dimensions';
import GalleryCell from '@/components/GalleryCell';
import InheritableValue from '@/components/InheritableValue';
import LegalTextsCell from '@/components/LegalTextsCell';
import List from '@/components/List';
import ListCell from '@/components/ListCell';
import Loader from '@/components/Loader';
import Memory from '@/components/Memory';
import productWithRevisionsQuery
  from '@/api/queries/product-with-revisions.graphql';
import RevisionManager from '@/components/RevisionManager';
import SeparatedList from '@/components/SeparatedList';
import SubmitButton from '@/components/SubmitButton';
import Submittable from '@/mixins/Submittable';
import TextPlaceholder from '@/components/TextPlaceholder';
import Thumbnail from '@/components/Thumbnail';
import Toast from '@/helpers/Toast';
import View from '@/mixins/View';
import Weight from '@/components/Weight';

export default {
  name: 'product-revisions-view',
  mixins: [Submittable, View],
  components: {
    Color,
    DefinitionList,
    Dimensions,
    GalleryCell,
    InheritableValue,
    LegalTextsCell,
    List,
    ListCell,
    Loader,
    Memory,
    RevisionManager,
    SeparatedList,
    SubmitButton,
    TextPlaceholder,
    Thumbnail,
    Weight,
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    isSuccessfullyLoaded() {
      return this.isLoaded && this.product;
    },
    fields() {
      const fields = [
        'altName',
        'deliveredItems',
        'usps',
        'summaryTexts',
        'features',
        'description',
        'keywords',
        'legalTexts',
        'media',
        'alwaysDisplayed',
        'pxsdbSecureBufferDisabled',
        'tags',
        'storage',
        'memory',
        'size',
        'stockSkipUpdate',
        'stockSkipUpdateFrom',
        'stockSkipUpdateTo',
      ];
      if (this.product.bundle) {
        pull(fields, 'deliveredItems', 'features');
      }
      return fields;
    },
  },
  apollo: {
    product: {
      query: productWithRevisionsQuery,
      variables() {
        return { id: this.id };
      },
      fetchPolicy: 'network-only',
      loadingKey: 'loading',
      update({ product }) {
        return product;
      },
      skip() {
        return !this.isAuthenticated;
      },
    },
  },
  methods: {
    addRevision() {
      this.$router.push({
        name: 'new-product-revision',
        params: { productId: this.id },
      });
    },
    editRevision(revision) {
      this.$router.push({
        name: 'edit-product-revision',
        params: { id: revision.id },
      });
    },
    async deleteRevision(revision) {
      await this.$apollo.mutate({
        mutation: deleteProductRevisionMutation,
        variables: { id: revision.id },
        refetchQueries: [
          {
            query: productWithRevisionsQuery,
            variables: { id: this.id },
          },
        ],
      });
      Toast.success(this.$t('toasts.deleted'));
    },
  },
  i18n: {
    messages: {
      de: {
        confirmations: {
          delete: 'Möchtest du die ausgewählte Revision wirklich löschen?',
        },
        toasts: {
          deleted: 'Die Revision wurde gelöscht',
        },
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// RevisionsView.vue?8a834048